import { z } from 'zod';
/** Angebotene Stufen (die Diamant-Stufen sind zurückgestellt). */
export const LEVEL_NAMES = [
    'NFIAP',
    'AFIAP',
    'EFIAP',
    'EFIAP_b',
    'EFIAP_s',
    'EFIAP_g',
    'EFIAP_p',
];
/** Schema einer Stufe. */
export const levelSchema = z.enum(LEVEL_NAMES);
/** Kennzahlen eines Benutzers. */
export const userStatsSchema = z.object({
    acceptances: z.number().int(),
    rawAcceptances: z.number().int(),
    overflow: z.number().int(),
    images: z.number().int(),
    countries: z.number().int(),
    awards: z.number().int(),
});
/** Voraussetzungen einer Stufe. */
export const levelRequirementSchema = z.object({
    acceptances: z.number().int(),
    images: z.number().int(),
    countries: z.number().int(),
});
/** Fortschritt je Kriterium (0 bis 1). */
export const progressSchema = z.object({
    acceptances: z.number(),
    images: z.number(),
    countries: z.number(),
});
/** Fortschritt für eine Stufe. */
export const levelProgressSchema = z.object({
    level: levelSchema,
    requirement: levelRequirementSchema,
    progress: progressSchema,
    fulfilled: z.boolean(),
});
/** Abfrageparameter von `/analytics/progress`. */
export const progressQuerySchema = z.object({ level: levelSchema });
/** Übersicht des Dashboards: Kennzahlen, Vorschlag und Fortschritt aller Stufen. */
export const analyticsSummarySchema = z.object({
    rulesYear: z.number().int(),
    stats: userStatsSchema,
    suggested: levelSchema.nullable(),
    levels: z.array(levelProgressSchema),
});
/** Abfrageparameter der Ranglisten. */
export const limitQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(10),
});
/** Bild mit Kennzahlen (Top-Bilder, Überlauf). */
export const imageStatSchema = z.object({
    imageId: z.number().int(),
    title: z.string(),
    acceptances: z.number().int(),
    raw: z.number().int(),
    overflow: z.number().int(),
    awards: z.number().int(),
    countries: z.number().int(),
});
/** Bild mit Restpotenzial. */
export const potentialRowSchema = z.object({
    imageId: z.number().int(),
    title: z.string(),
    category: z.string().nullable(),
    acceptances: z.number().int(),
    potential: z.number().int(),
});
/** Flop-Bild. */
export const flopRowSchema = z.object({
    imageId: z.number().int(),
    title: z.string(),
    category: z.string().nullable(),
    submissions: z.number().int(),
});
/** Zeile einer Erfolgsquote. */
export const rateRowSchema = z.object({
    key: z.string(),
    decided: z.number().int(),
    accepted: z.number().int(),
    rate: z.number(),
});
/** Abfrageparameter der Erfolgsquote. */
export const rateQuerySchema = z.object({
    by: z.enum(['region', 'country', 'category', 'medium']).default('region'),
});
/** Region mit Quote und Ländern. */
export const regionRowSchema = rateRowSchema.extend({ countries: z.array(z.string()) });
/** Land mit Annahmen. */
export const countryRowSchema = z.object({ country: z.string(), acceptances: z.number().int() });
/** Land ohne Annahme. */
export const countryGapSchema = z.object({ country: z.string(), region: z.string().nullable() });
/** Bestes Bild einer Region. */
export const bestPerRegionSchema = z.object({
    region: z.string(),
    imageId: z.number().int(),
    title: z.string(),
    acceptances: z.number().int(),
});
/** Land ohne Annahme, in dem noch Wettbewerbe mit offenem Einsendeschluss laufen. */
export const countryOpportunitySchema = z.object({
    country: z.string(),
    region: z.string().nullable(),
    /** Anzahl der Wettbewerbe mit offenem Einsendeschluss in diesem Land. */
    openSalons: z.number().int(),
    /** Nächster Einsendeschluss (Datum `JJJJ-MM-TT`). */
    nextClosing: z.string(),
    /** Der Wettbewerb mit dem nächsten Einsendeschluss. */
    nextSalon: z.object({ id: z.number().int(), name: z.string(), fiapNumber: z.string() }),
});
/** Abfrage der Vorschläge „Neu versuchen". */
export const retryQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(5),
    /** `best`: je Bild nur der beste Wettbewerb (Standard); `all`: alle Paare. */
    perImage: z.enum(['best', 'all']).default('best'),
});
/** Vorschlag: dieses Bild in diesem offenen Wettbewerb (erneut) einreichen. */
export const retrySuggestionSchema = z.object({
    imageId: z.number().int(),
    title: z.string(),
    rate: z.number(),
    /** Entschiedene Einreichungen des Bildes (Grundlage der Quote). */
    decided: z.number().int(),
    /** Davon Annahmen. */
    accepted: z.number().int(),
    score: z.number(),
    newCountry: z.boolean(),
    salon: z.object({
        id: z.number().int(),
        fiapNumber: z.string(),
        name: z.string(),
        country: z.string().nullable(),
        region: z.string().nullable(),
        closingDate: z.string(),
    }),
});
/** Antwort: Vorschläge „Neu versuchen". */
export const retryListSchema = z.object({ items: z.array(retrySuggestionSchema) });
/** Antwort: Länder mit offenen Wettbewerben, aus denen noch keine Annahme vorliegt. */
export const countryOpportunityListSchema = z.object({ items: z.array(countryOpportunitySchema) });
/** Antwort: Bilder mit Kennzahlen. */
export const imageStatListSchema = z.object({ items: z.array(imageStatSchema) });
/** Antwort: Bilder mit Restpotenzial. */
export const potentialListSchema = z.object({ items: z.array(potentialRowSchema) });
/** Antwort: Flops. */
export const flopListSchema = z.object({ items: z.array(flopRowSchema) });
/** Antwort: Erfolgsquoten. */
export const rateListSchema = z.object({ items: z.array(rateRowSchema) });
/** Antwort: Regionen. */
export const regionListSchema = z.object({ items: z.array(regionRowSchema) });
/** Antwort: Länder. */
export const countryListSchema = z.object({ items: z.array(countryRowSchema) });
/** Antwort: Länderlücken. */
export const countryGapListSchema = z.object({ items: z.array(countryGapSchema) });
/** Antwort: bestes Bild je Region. */
export const bestPerRegionListSchema = z.object({ items: z.array(bestPerRegionSchema) });
//# sourceMappingURL=analytics.js.map